import { Component, effect, inject, Input, OnChanges, signal, SimpleChanges } from '@angular/core';
import { BusinessAccount } from '@/app/common/general-types/business-account.dto';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { environment } from '@/environments/environment';
import { CurrencyPipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { BrandInsightsState } from '../../brand-insights.state';

@Component({
  selector: 'brand-ins-billing',
  imports: [CurrencyPipe],
  templateUrl: './billing.html',
  styleUrl: './billing.css',
  host: { class: 'block h-full min-h-0 w-full min-w-0' },
})
export class Billing implements OnChanges {
  @Input() brand: BusinessAccount | undefined;

  private currentUserService = inject(CurrentUserService);
  private brandState = inject(BrandInsightsState);

  subscription = signal<any | null>(null);
  isLoading = signal<boolean>(false);

  constructor() {
    effect(() => {
      const fromState = this.brandState.brand();
      if (fromState && fromState.id !== this.brand?.id) {
        this.brand = fromState;
        void this.loadSubscription(fromState);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['brand']) {
      await this.loadSubscription(this.brand);
    }
  }

  private async loadSubscription(brand: BusinessAccount | undefined): Promise<void> {
    if (brand?.stripeCustomerId) {
      this.isLoading.set(true);
      this.subscription.set(null);
      try {
        const sub = await firstValueFrom(
          this.currentUserService.findSubscriptionForCustomer(brand.stripeCustomerId, '')
        );
        this.subscription.set(sub);
      } catch {
        this.subscription.set(null);
      } finally {
        this.isLoading.set(false);
      }
    } else {
      this.subscription.set(null);
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-200 dark:ring-emerald-700/40';
      case 'trialing': return 'bg-primary/10 text-accent ring-primary/25 dark:bg-primary/25 dark:text-foreground dark:ring-primary/35';
      case 'canceled': return 'bg-red-50 text-red-800 ring-red-600/20 dark:bg-red-900/30 dark:text-red-200 dark:ring-red-700/40';
      case 'past_due': return 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-700/40';
      default: return 'bg-zinc-100 text-zinc-600 ring-zinc-500/20 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-600/40';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'active': return 'Active';
      case 'trialing': return 'Trialling';
      case 'canceled': return 'Cancelled';
      case 'past_due': return 'Past Due';
      default: return status ?? 'Unknown';
    }
  }

  getProductName(productId: string): string {
    if (productId === environment.stripe.shaas21products.sub_essential) return 'Essential Plan';
    if (productId === environment.stripe.shaas21products.bo_ai_moderation) return 'AI Video Moderation';
    if (productId === environment.stripe.shaas21products.bo_gifting_seeding) return 'Gifting Seeding';
    if (productId === environment.stripe.shaas21products.bo_affiliates_shopify) return 'Affiliates Shopify';
    if (productId === environment.stripe.shaas21products.bo_affiliates_rakuten) return 'Affiliates Rakuten';
    if (productId === environment.stripe.shaas21products.bo_monitoring) return 'Social Monitoring';
    return 'Bolt-on';
  }

  isBaseSub(item: any): boolean {
    return item.price?.product?.id === environment.stripe.shaas21products.sub_essential
      || item.price?.product?.id === environment.stripe.products?.essentials;
  }

  boltOns(sub: any): any[] {
    return (sub?.items?.data ?? []).filter((item: any) => !this.isBaseSub(item));
  }

  getBoltOnPrice(item: any): number {
    const quantity = typeof item?.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
    return ((item.price?.unit_amount ?? 0) / 100) * quantity;
  }

  getBillingPeriod(item: any): string {
    return item.price?.recurring?.interval === 'month' ? 'Monthly' : 'Yearly';
  }

  getCurrencyCode(sub: any): string {
    return (sub?.currency ?? 'gbp').toUpperCase();
  }
}
